import PropTypes from 'prop-types';
import { Set } from 'immutable';

import { cachedHashString } from './hash';

export class Enum {
  /** @type {Set<Enum>} */
  static get values() {
    if (this.values_implementation) {
      return this.values_implementation;
    }

    throw new Error('Not implemented!');
  }

  /** @type {Array<Enum>} */
  static get valueArray() {
    if (this.valueArray_implementation) {
      return this.valueArray_implementation;
    }

    throw new Error('Not implemented!');
  }

  /** @type {Function} */
  static get propType() {
    if (this.propType_implementation) {
      return this.propType_implementation;
    }

    throw new Error('Not implemented!');
  }

  /**
   * @param {string|Enum} key
   * @returns {boolean}
   */
  static has(key) {
    return this.valueArray.some(v => (key === v || key === v.value));
  }

  /** @type {string} */
  value;

  constructor(value) {
    if (new.target?.frozen) {
      throw new Error(`You cannot instantiate custom values of ${new.target.name}!`);
    }

    this.value = value;
  }

  toString() { return this.value }

  toJSON() { return this.toString() }

  valueOf() { return this.toString() }

  equals(other) {
    return (this === other);
  }

  hash() {
    cachedHashString(`${this.constructor.name}.${this.value}`);
  }
}

function proxiedGetEnumValue(obj, prop) {
  if (prop in obj) {
    return obj[prop];
  }

  throw new Error(`Unknown enum value ${obj.name ?? 'Enum'}.${prop}. Valid values are: ${obj.valueArray}`);
}

/**
 * @template T
 * @param {T} enumClass
 * @returns {T}
 */
export function decorateEnumClass(enumClass) {
  const keys = Object.keys(enumClass);
  keys.forEach(value => {
    // eslint-disable-next-line no-param-reassign
    enumClass[value] = new enumClass(value);
  });

  const values = Object.values(enumClass);
  Object.defineProperties(enumClass, {
    valueArray_implementation: {
      value: Object.freeze(values),
      enumerable: false
    },
    values_implementation: {
      value: Set(values),
      enumerable: false
    },
    propType_implementation: {
      value: PropTypes.instanceOf(this),
      enumerable: false
    },
    frozen: {
      value: true,
      enumerable: false
    },
    $$typeof: { // Needed for hot loading
      value: undefined,
      enumerable: false
    },
    displayName: { // Needed for hot loading
      value: undefined,
      enumerable: false
    }
  });

  Object.freeze(enumClass);

  if (process.env.NODE_ENV === 'development') {
    return new Proxy(enumClass, { get: proxiedGetEnumValue });
  }

  return enumClass;
}

export default function genEnum(...values) {
  Object.freeze(values);

  const obj = values.reduce((r, value) => {
    r[value] = value; // eslint-disable-line no-param-reassign
    return r;
  }, {});

  const valueSet = Set(values);
  Object.defineProperties(obj, {
    values: {
      enumerable: false,
      value: valueSet
    },
    valueArray: {
      enumerable: false,
      value: values
    },
    has: {
      enumerable: false,
      value: val => valueSet.has(val)
    },
    propType: {
      enumerable: false,
      value: PropTypes.oneOf(values)
    },
    $$typeof: { // Needed for hot loading
      enumerable: false,
      value: undefined
    }
  });

  Object.freeze(obj);

  if (process.env.NODE_ENV === 'development') {
    return new Proxy(obj, { get: proxiedGetEnumValue });
  }

  return obj;
}
